import { ROLE_DEFINITIONS } from "./roles";
import type { ModuleKey } from "../modules/registry";
import { MODULE_VISIBILITY } from "./visibility";
import type { LegacyAccessFlag, Permission, Role, User, UserStatus } from "./types";
import { resolveEffectivePermissions } from "./effectiveAccess";

export type AccessRule = {
  roles?: Role[];
  anyPermissions?: Permission[];
  allPermissions?: Permission[];
};

export function normalizeUserStatus(status?: User["status"]): UserStatus {
  if (status === "disabled") return "disabled";
  if (status === "inactive" || status === "locked") return "inactive";
  return "active";
}

export function isUserActive(user: Pick<User, "status"> | null | undefined) {
  return normalizeUserStatus(user?.status) === "active";
}

export function isUserDisabled(user: Pick<User, "status"> | null | undefined) {
  return normalizeUserStatus(user?.status) === "disabled";
}

export function canDeleteUser(user: Pick<User, "status"> | null | undefined) {
  return isUserDisabled(user);
}

export function getRoleDefinition(role?: Role) {
  return ROLE_DEFINITIONS[role ?? "user"] ?? ROLE_DEFINITIONS.user;
}

export function getRolePermissions(role?: Role): Permission[] {
  if (!role) return [];
  return getRoleDefinition(role).permissions;
}

export function getLegacyFlagsForRole(role?: Role): LegacyAccessFlag[] {
  if (!role) return [];
  return getRoleDefinition(role).legacyFlags;
}

export function getUserPermissions(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess"> | null | undefined): Permission[] {
  return resolveEffectivePermissions(user);
}

export function hasPermission(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess"> | null | undefined, permission: Permission) {
  return getUserPermissions(user).includes(permission);
}

export function hasRole(user: Pick<User, "role" | "assignedRoles"> | null | undefined, role: Role) {
  return user?.role === role || user?.assignedRoles?.includes(role) || false;
}

export function canAccessRule(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess"> | null | undefined, rule?: AccessRule) {
  if (!rule) return true;
  if (isAdminUser(user)) return true;

  const checks: boolean[] = [];
  if (rule.roles?.length) {
    checks.push(rule.roles.some((role) => hasRole(user, role)));
  }
  if (rule.anyPermissions?.length) {
    checks.push(hasAnyPermission(user, rule.anyPermissions));
  }
  if (rule.allPermissions?.length) {
    checks.push(hasAllPermissions(user, rule.allPermissions));
  }

  return checks.length === 0 ? true : checks.some(Boolean);
}

// ── Per-hotel scoped access ─────────────────────────────────────────────────
// The backend enforces permissions per hotel; these helpers let the UI hide
// actions the API would reject at the currently selected hotel. Roles are still
// evaluated globally (a role is a user-level attribute); only permission checks
// are scoped to the hotel's granted set.

/** Expanded permissions the user holds AT a specific hotel. Admins keep the full
 *  set; if the hotel isn't in the breakdown (unknown/not-yet-loaded) fall back to
 *  the global union so visibility never regresses below what the API allows. */
export function permissionsForHotel(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "propertyPermissions"> | null | undefined,
  hotelId: string | null | undefined,
): Set<Permission> {
  if (!user) return new Set();
  if (isAdminUser(user)) return new Set(getUserPermissions(user));
  const breakdown = user.propertyPermissions;
  if (!hotelId || !Array.isArray(breakdown)) return new Set(getUserPermissions(user));
  const entry = breakdown.find((p) => String(p.hotelId) === String(hotelId));
  if (!entry) return new Set(getUserPermissions(user));
  return new Set(entry.permissions ?? []);
}

/** Like canAccessRule but evaluates anyPermissions/allPermissions against the
 *  permissions granted at `hotelId`. Roles remain global. Pass a null hotelId to
 *  fall back to the global evaluation. */
export function canAccessRuleAtHotel(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "propertyPermissions"> | null | undefined,
  rule: AccessRule | undefined,
  hotelId: string | null | undefined,
) {
  if (!rule) return true;
  if (isAdminUser(user)) return true;
  if (!hotelId) return canAccessRule(user, rule);

  const hotelPerms = permissionsForHotel(user, hotelId);
  const checks: boolean[] = [];
  if (rule.roles?.length) {
    checks.push(rule.roles.some((role) => hasRole(user, role)));
  }
  if (rule.anyPermissions?.length) {
    checks.push(rule.anyPermissions.some((permission) => hotelPerms.has(permission)));
  }
  if (rule.allPermissions?.length) {
    checks.push(rule.allPermissions.every((permission) => hotelPerms.has(permission)));
  }

  return checks.length === 0 ? true : checks.some(Boolean);
}

export function hasAnyPermission(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess"> | null | undefined, permissions: Permission[]) {
  const userPermissions = new Set(getUserPermissions(user));
  return permissions.some((permission) => userPermissions.has(permission));
}

export function hasAllPermissions(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess"> | null | undefined, permissions: Permission[]) {
  const userPermissions = new Set(getUserPermissions(user));
  return permissions.every((permission) => userPermissions.has(permission));
}

export function isAdminUser(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess"> | null | undefined) {
  return hasPermission(user, "admin.access");
}

export function canAccessModule(user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess"> | null | undefined, moduleKey: ModuleKey) {
  return canAccessRule(user, MODULE_VISIBILITY[moduleKey]);
}
